// Copyright (c) Docugami, Inc. All rights reserved.

import { ContextualMenu, DefaultButton, IconButton, Modal, PrimaryButton, Stack } from "@fluentui/react";
import { createFolderId } from "components/file_management/file_helpers";
import { homeGray } from "components/shell/presentation/AppStyles";
import { useAddEntryWithInstances } from "hooks/AddEntryWithInstances";
import { DgmlInstancesSingleton } from "hooks/documentDgml";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useSdvEntityQuery } from "hooks/SdvEntityQuery";
import { useTagText } from "hooks/TagText";
import { TokenRange } from "networking/api";
import { useCallback, useEffect, useMemo } from "react";
import { MultiSelectComponent } from "sdv/components/MultiSelectComponent";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";
import { ComponentId } from "sdv/engine/Component";
import { Engine } from "sdv/engine/Engine";
import { createFocusViewEntity } from "sdv/engine/EntityFactory";
import { selectMappedTokens } from "store/dgml/dgmlSelectors";
import { setSelectedLabel } from "store/reports/reportsSlice";
import { selectEditedChunkInstance } from "store/sdvSelectors";
import styled from "styled-components";
import { AutomationId } from "utils/Automation";
import { SpanIdUtils } from "utils/SpanIdUtils";
import { TokenRangeUtils } from "utils/TokenRangeUtils";
import { CardHeader } from "./CardHeader";
import { ModalCardletStyle } from "./CardStyles";
import { ChunkTagInputField } from "./ChunkTagInputField";
import { ItemCardlet } from "./ItemCardlet";
import { ChunkSlotItem, isChunkSlot, ISlotItem, SlotItemType } from "./ItemSlot";
import { ItemSlots } from "./ItemSlots";

const ContentRoot = styled.div`
    display: flex;
    flex-direction: column;
`

const multiSelectComponents = [ComponentId.MultiSelect, ComponentId.TokenRange];
const activeMultiSelectComponents = [ComponentId.MultiSelect, ComponentId.TokenRange, ComponentId.Selected];

interface LabelEditorProps {
    label?: string;
    clusterId: string;
    documentId: string;
    engine: Engine | null;
    onCancel?: () => void;
    onConfirm?: (spanIds: string[]) => void;
}

const cardletStyles = { root: [ModalCardletStyle] };
export const LabelEditor = (props: LabelEditorProps): JSX.Element => {
    const { label, documentId, engine, onConfirm, onCancel } = props;
    const fileId = createFolderId(documentId).fileId;
    const editedChunkInstance = useAppSelector(selectEditedChunkInstance);
    const mappedTokens = useAppSelector(state => selectMappedTokens(state, props.clusterId, fileId));

    const dispatch = useAppDispatch();
    const { canAddEntry: canAddToReport, addEntry: addToReport } = useAddEntryWithInstances(props.label, props.clusterId, () => dispatch(setSelectedLabel(undefined)));

    const tagText = useTagText(props.clusterId, documentId);
    const { setTagText } = tagText;
    useEffect(() => {
        if (!!label) {
            setTagText(label);
        }
    }, [label, setTagText]);

    const { entities } = useSdvEntityQuery(multiSelectComponents, engine);
    const { entities: activeEntities } = useSdvEntityQuery(activeMultiSelectComponents, engine);

    const [spanIds, spanToTokenRangeLookup] = useMemo((): [string[], { [id: string]: TokenRange }] => {
        if (!DgmlInstancesSingleton.isInitialized(documentId)) {
            return [[], {}];
        }

        const spanIds: string[] = [];
        const lookup: { [id: string]: TokenRange } = {};

        // Create chunk slot span ids from highlight entities
        const activeEntity = activeEntities[0];
        const activeIdx = entities.indexOf(activeEntity);

        entities.forEach((e, idx) => {
            let spanId = e.getComponent<MultiSelectComponent>(ComponentId.MultiSelect)!.spanId
            let tokenRange = e.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
            if (idx === activeIdx && !!editedChunkInstance?.spanId) {
                // Replace the active highlight entity with the edit token range
                spanId = editedChunkInstance.spanId;
                tokenRange = editedChunkInstance.tokenRange;
            }
            else {
                // It's possible that the token range was edited for a span. Use the range if it is different from the range for the span
                const span = SpanIdUtils.parse(spanId);
                const spanTokenRange = SpanIdUtils.getTokenRange(mappedTokens, span) ?? tokenRange;
                if (!TokenRangeUtils.rangesEqual(spanTokenRange, tokenRange)) {
                    spanId = DgmlInstancesSingleton.getSpanIdForTokenRange(documentId, tokenRange, span.subtreeId);
                }
            }

            spanIds.push(spanId);
            lookup[spanId] = tokenRange;
        });

        return [spanIds, lookup];
    }, [documentId, entities, mappedTokens, activeEntities, editedChunkInstance]);

    const slots = useMemo((): ChunkSlotItem[] => {
        function createSlot(spanId?: string): ChunkSlotItem {
            const item: ChunkSlotItem = !!spanId ? {
                id: spanId,
                type: SlotItemType.Chunk,
                spanId: spanId,
            } : {
                id: 'empty-slot',
                type: SlotItemType.Chunk,
                spanId: '',
            };

            return item;
        }

        if (!spanIds.length) {
            return [createSlot()];
        }
        else {
            return spanIds.map(createSlot);
        }
    }, [spanIds]);

    const confirm = useCallback(() => {
        const confirmSpanIds = slots.map(x => x.spanId).filter(x => !!x) as string[];
        onConfirm?.(confirmSpanIds);
    }, [onConfirm, slots])

    const header = (
        <Stack horizontal horizontalAlign="space-between" styles={{ root: { alignItems: "center" } }}>
            <Stack.Item styles={{ root: { flex: 1, height: "fit-content" } }}>
                <ChunkTagInputField tagText={tagText} disabled />
            </Stack.Item>
            <IconButton onClick={onCancel} title="Discard feedback" iconProps={{ iconName: "ChromeClose" }} styles={{ root: { marginLeft: 'auto' }, icon: { color: homeGray } }} />
        </Stack>
    )

    const footer = (
        <Stack horizontal horizontalAlign="space-between" tokens={{ childrenGap: '10px' }} styles={{ root: { width: '100%', padding: '3px 0px' } }}>
            {canAddToReport ? <DefaultButton text="Add to report" title="Add label to report" iconProps={{ iconName: "CircleAddition" }} styles={{ root: { minWidth: 'unset', border: 'none', padding: '0px 5px' }, icon: { height: "14px" } }} onClick={() => addToReport(props.documentId, spanIds)} /> : <div />}
            <PrimaryButton primary text="Confirm" title="Complete feedback" iconProps={{ iconName: "SkypeCheck" }} onClick={confirm} />
        </Stack>
    )

    function onClickSlot(item: ISlotItem) {
        if (!isChunkSlot(item)) {
            return;
        }

        const tokenRange = spanToTokenRangeLookup[item.spanId ?? ''];
        if (!!tokenRange && !!engine) {
            engine?.queuePreUpdateCommand((entityManager, resources) => {
                if (resources.set("selectedEntityName", item.spanId ?? '')) {
                    createFocusViewEntity(entityManager, tokenRange, true);
                }
            });
        }
    }

    function removeSlot(item: ISlotItem) {
        if (!isChunkSlot(item)) {
            return;
        }

        const targetEntity = entities.find(e => e.getComponent<MultiSelectComponent>(ComponentId.MultiSelect)!.spanId === item.spanId);
        targetEntity?.destroy();
    }

    return (
        <ItemCardlet
            automationId={AutomationId.WorkPathDocumentItemCardlet}
            header={header}
            footer={footer}
            styles={cardletStyles}>
            <ContentRoot>
                <CardHeader count={slots.length} />
                <ItemSlots
                    slots={slots}
                    onClick={onClickSlot}
                    onRemove={removeSlot}
                    hideManagementCommands />
            </ContentRoot>
        </ItemCardlet>
    );
}

interface ModalLabelEditorProps extends LabelEditorProps {
    isOpen: boolean;
    onDismiss?: () => void;
}

export const ModalLabelEditor = (props: ModalLabelEditorProps): JSX.Element => {
    return (
        <Modal
            isOpen={props.isOpen}
            isBlocking={false}
            isDarkOverlay={false}
            isModeless={true}
            onDismiss={props.onCancel}
            isClickableOutsideFocusTrap={true}
            forceFocusInsideTrap={false}
            styles={{
                root: { width: 'fit-content', height: 'fit-content', right: '300px', top: '230px' },
                main: { minHeight: 'unset', width: '400px', borderRadius: '5px' }
            }}
            dragOptions={{
                moveMenuItemText: 'Move',
                closeMenuItemText: 'Close',
                menu: ContextualMenu,
                dragHandleSelector: ".dragTarget"
            }}>
            <div className='dragTarget' style={{ cursor: 'move' }}>
                <LabelEditor {...props} />
            </div>
        </Modal>
    )
}